import { useQuery } from '@apollo/client'
import { Box, Stack, Text, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { isAddress } from 'viem'

import { NO_PROFILE_ADDRESSES } from '@/constants/blockchain'
import { useErrorMessages } from '@/constants/content'
import { useAdminsContext } from '@/contexts/AdminsContext'
import useL1NS from '@/hooks/useL1NS'
import { useAuth } from '@/plugins/auth'
import ConnectMiddleware from '@/plugins/auth/RouteRedirectors/ConnectMiddleware'
import { GetUserStats, QUERY_GET_USER_STATS } from '@/schemas/user.schema'
import { BodyStack } from '@/styled/components'
import { ErrorMessageDisplay } from '@/utils/utils'

import RootButton from '../Buttons/RootButton'
import ContentFlagged from '../ContentFlagged'
import ErrorMessage from '../ErrorMessage'
import HelpDrawer from '../HelpDrawer/HelpDrawer'
import Loading from '../Loading'
import FlagContentModal from '../Modals/Content/FlagContentModal'
import ModalSkeleton from '../Modals/ModalSkeleton'
import NotFound from '../NotFound'
import Seo from '../Seo'
import { loadProfile, ProfileMetadata } from './metadata'
import classes from './Profile.module.css'
import ProfileHeader from './ProfileHeader'
import ProfileItems from './ProfileItems'

const Component: React.FC = () => {
  const { id } = useParams()
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { getL1nsProfile, getL1nsAddress, getL1nsName } = useL1NS()
  const { address, userIdentity } = useAuth()
  const { admins } = useAdminsContext()
  const errorMessages = useErrorMessages()

  const [opened, { open, close }] = useDisclosure(false)

  // Is admin?
  const isAdmin = useMemo(
    () => admins.map(a => a.toLowerCase()).includes(address?.toLowerCase() || ''),
    [address, admins]
  )

  // State Variables
  const [profile, setProfile] = useState<ProfileMetadata>({
    name: 'No Name',
    description: '',
    image: '',
    creator: {
      name: '',
      link: '',
    },
    bannerImage: '',
  })
  const [pageNotFound, setPageNotFound] = useState<boolean>(false)
  const [profileAddr, setProfileAddr] = useState<string | undefined>(undefined)
  const [profileUsername, setProfileUsername] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<ErrorMessageDisplay>()
  const [refetchUsername, setRefetchUsername] = useState<boolean>(false)

  // Query
  const {
    data,
    loading: loadingQuery,
    refetch,
  } = useQuery<GetUserStats>(QUERY_GET_USER_STATS, {
    skip: profileAddr === undefined,
    variables: {
      id: profileAddr?.toLowerCase(),
    },
  })

  // Is profile flagged?
  const flagged = useMemo(() => data?.user?.flagged || false, [data])

  // Is my profile?
  const isMyProfile = useMemo(
    () => (address && profileAddr && profileAddr.toLowerCase() === address.toLowerCase()) || false,
    [profileAddr, address]
  )

  // URL
  const siteUrl = window.location.origin

  // Memoized Variables
  const publicPageUrl = useMemo(() => `${siteUrl}/public/${id}`, [siteUrl, id])
  const CustomStack = useMemo(() => (isMobile ? Stack : BodyStack), [isMobile])

  // convert id wich may be a username to an address, if not username, it will return the same id
  const convertUsernameIntoAddress = useCallback(
    async (user: string) => {
      if (!isAddress(user)) {
        const addrs = await getL1nsAddress(user)
        return addrs
      }
      return user
    },
    [getL1nsAddress]
  )

  // Steps:
  const loadPageProfile = useCallback(async () => {
    setLoading(true)
    // 0. Check if id is a valid string
    if (!id && typeof id !== 'string') {
      setPageNotFound(true)
      setLoading(false)
      return
    }
    try {
      // 1. Convert username into address
      const addrs = await convertUsernameIntoAddress(id)
      // check if NO_PROFILE_ADDRESSES includes the address, but make sure capitalization does not matter
      if (NO_PROFILE_ADDRESSES.map(addr => addr.toLowerCase()).includes(addrs.toLowerCase())) {
        setPageNotFound(true)
        setLoading(false)
        return
      }
      setProfileAddr(addrs)

      // 2. Get username from L1NS
      // Note: always do this regardless of username that leads to the profile,
      // since the active username the address is using might be different!
      const username = await getL1nsName(addrs)
      if (username) {
        setProfileUsername(username)
      } else {
        setProfileUsername(undefined)
      }

      // 3. Get profile url from contract
      const profileUrl = await getL1nsProfile(addrs)

      // 4. Load profile from IPFS
      if (profileUrl) {
        const p = await loadProfile(profileUrl)
        setProfile(p as ProfileMetadata)
      } else {
        // profile with default values
        setProfile({
          name: 'No Name',
          description: '',
          image: '',
          creator: {
            name: '',
            link: '',
          },
          bannerImage: '',
        })
      }
      setLoading(false)
    } catch (e: any) {
      console.error(e.message)
      setError(
        e.message.includes('Failed to fetch')
          ? {
              message: 'Error fetching profile details',
              details: errorMessages.rpcConnection,
            }
          : {
              message: 'Error fetching profile details',
              details: e.message,
            }
      )
      setLoading(false)
    }
  }, [id, convertUsernameIntoAddress, getL1nsProfile, loadProfile, setPageNotFound, setLoading])

  useEffect(() => {
    loadPageProfile()
  }, [id, refetchUsername])

  useEffect(() => {
    if (userIdentity?.address === profileAddr) {
      setProfile({
        name: userIdentity?.nickname || 'No Name',
        creator: {
          name: userIdentity?.address || '',
          link: userIdentity?.link || '',
        },
        description: userIdentity?.bio || 'No Description',
        image: userIdentity?.profileImage || '',
        bannerImage: userIdentity?.bannerImage || '',
      })
    }
  }, [userIdentity])

  const handleClose = useCallback(() => {
    close()
    refetch()
  }, [close, refetch])

  return (
    <ConnectMiddleware>
      {profile && (
        <Seo
          title={profile?.name || 'Profile'}
          description={profile.description}
          type="website"
          image={
            profile?.image ||
            'https://lamina1.mypinata.cloud/ipfs/QmXZdRodUFQ48nFkEz3Docd7ryaEjMDUEi3Yh2D99U6f8A'
          }
          creator={profile?.creator?.name || 'Lamina1'}
          url={publicPageUrl}
        />
      )}
      {pageNotFound && <NotFound />}
      {flagged && !isMyProfile && !isAdmin && <ContentFlagged contentType="Profile" />}
      {!pageNotFound && (loading || loadingQuery) && <Loading />}
      {!pageNotFound &&
        (!flagged || isMyProfile || isAdmin) &&
        !loading &&
        !loadingQuery &&
        profileAddr && (
          <CustomStack
            style={{
              marginTop: isMobile ? '90px' : '',
              width: isMobile ? '100%' : '',
            }}
          >
            <Stack style={{ gap: error ? '20px' : '10px' }}>
              {flagged && !isAdmin && (
                <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Box className={classes.flaggedBox}>
                    <Text className={classes.flaggedText}>
                      {t(
                        'common.ownProfileFlagged',
                        'Your profile has been flagged by LAMINA1 moderators. If you believe this is a mistake, please contact an admin in the LAMINA1 Discord.'
                      )}
                    </Text>
                  </Box>
                </Stack>
              )}
              {isAdmin && (
                <Stack style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                  {flagged ? (
                    <>
                      <Box className={classes.flaggedBox}>
                        <Text className={classes.flaggedText}>
                          {t('common.adminProfileFlagged', 'This profile is flagged.')}
                        </Text>
                      </Box>
                      <RootButton style1={false} error onClick={open}>
                        {t('common.unflag', 'Unflag')}
                      </RootButton>
                    </>
                  ) : (
                    <RootButton style1={false} error onClick={open}>
                      {t('common.flag', 'Flag')}
                    </RootButton>
                  )}
                </Stack>
              )}
              <ModalSkeleton
                opened={opened}
                onClose={handleClose}
                title={t('common.unflag', 'Unflag')}
              >
                <FlagContentModal
                  close={handleClose}
                  contentType="creators"
                  contentId={profileAddr.toLowerCase()}
                  isFlagged={flagged}
                />
              </ModalSkeleton>
              <ProfileHeader
                profileAddress={profileAddr}
                isMyProfile={isMyProfile}
                // Use empty string if profileUsername is undefined to prevent unnecessary refetch
                profileUsername={profileUsername || ''}
                profile={profile}
                setProfile={setProfile}
                setRefetchUsername={() => setRefetchUsername(prev => !prev)}
              />
              {error && <ErrorMessage message={error.message} details={error.details} />}
              <ProfileItems profileId={profileAddr} isMyProfile={isMyProfile} />
            </Stack>
          </CustomStack>
        )}
      <HelpDrawer id="profile" />
    </ConnectMiddleware>
  )
}
Component.displayName = 'Profile'

// eslint-disable-next-line import/prefer-default-export
export { Component }
